// Client-side helpers for Run of Show manual events and the event picker.
// The authoritative rules (and the same Activity list) live in
// Backend/Production_Packet_ROS.gs; rosActivity.test.js keeps them in sync.

export const ROS_ACTIVITY_OPTIONS = [
  // Alphabetical, with Other last: the fallback when no listed type applies.
  'Departure',
  'Fabrication Pickup',
  'Flip',
  'Load-In',
  'Load-Out Prep',
  'Return to Warehouse',
  'Site Visit',
  'Special Delivery',
  'Strike',
  'Supplier Drop Off',
  'Supplier Pick Up',
  'Other'
];

/**
 * Run of Show activity colors. The definition lives in
 * Backend/Production_Packet_ROS.gs (ROS_ACTIVITY_TONES), which also colors
 * the generated packet; this is its copy for the builder, and
 * RunOfShowPanel.test.js fails if the two differ. First match wins; anything
 * unrecognized is neutral.
 */
export const ROS_ACTIVITY_TONES = [
  { tone: 'load-in', label: 'Load-In', match: /\bload[\s-]?in\b/i, color: '#7faf9a' },
  { tone: 'strike', label: 'Strike', match: /\b(strike|load[\s-]?out)\b/i, color: '#b97b6f' },
  { tone: 'flip', label: 'Flip', match: /\bflip\b/i, color: '#7693a9' },
  { tone: 'departure', label: 'Departure', match: /\b(depart(ure)?|return to warehouse)\b/i, color: '#a79c8c' }
];
export const ROS_NEUTRAL_TONE = { tone: 'neutral', label: '', color: '#b5b3ad' };

function toneFor(activity) {
  const a = String(activity || '');
  return ROS_ACTIVITY_TONES.find((t) => t.match.test(a)) || ROS_NEUTRAL_TONE;
}

/** The indicator tone for an activity name ('neutral' when unrecognized). */
export function activityTone(activity) {
  return toneFor(activity).tone;
}

/** The indicator color for an activity name (the neutral color when unrecognized). */
export function activityColor(activity) {
  return toneFor(activity).color;
}

/** The stored Activity: the picked/typed text, or the custom name when "Other" is picked. */
export function resolveActivity(activity, customActivity) {
  const a = String(activity || '').trim();
  const custom = String(customActivity || '').trim();
  if (/^other$/i.test(a)) return custom;
  return a || custom;
}

/** Coordinator-facing problems that stop a manual event from being saved. */
export function manualEventErrors(ev, opts = {}) {
  const errors = [];
  if (!ev.dateISO) errors.push('Choose a date.');
  if (!String(ev.activity || '').trim()) errors.push('Choose or type an activity.');
  else if (/^other$/i.test(String(ev.activity).trim()) && !String(ev.customActivity || '').trim()) {
    errors.push('Enter the activity name.');
  }
  if (opts.needsTimeZone && !ev.timeZone) errors.push('Choose the time zone for this activity.');
  if (ev.locationRef === 'new') {
    if (!String(ev.newLocationName || '').trim()) errors.push('Enter the location name.');
    if (!String(ev.newLocationAddress || '').trim()) errors.push('Enter the location address.');
  }
  return errors;
}

/**
 * Operational order used to place rows (Backend ROS_ACTIVITY_RANK; kept in
 * sync by rosActivity.test.js). Only used to show a row being saved in its
 * place until the server's sorted Run of Show arrives.
 */
export const ROS_ACTIVITY_RANK = [
  'Departure', 'Depart Warehouse', 'Supplier Pick Up', 'Fabrication Pickup', 'Special Delivery', 'Load-In',
  'Site Visit', 'Walkthrough', 'Flip', 'Room Turn', 'Reset', 'Pickup', 'Load-Out Prep', 'Strike',
  'Supplier Drop Off', 'Return Home', 'Return to Warehouse'
];

function activityRank(activity) {
  const a = String(activity || '').trim().toLowerCase();
  const i = ROS_ACTIVITY_RANK.findIndex((r) => r.toLowerCase() === a);
  return i === -1 ? ROS_ACTIVITY_RANK.length : i;
}

const isTimed = (e) => e.timeMinutes !== null && e.timeMinutes !== undefined;

function compareEvents(a, b) {
  if (a.dateISO !== b.dateISO) return a.dateISO < b.dateISO ? -1 : 1;
  if (isTimed(a) !== isTimed(b)) return isTimed(a) ? -1 : 1;
  if (isTimed(a) && a.timeMinutes !== b.timeMinutes) return a.timeMinutes - b.timeMinutes;
  const ra = activityRank(a.activity);
  const rb = activityRank(b.activity);
  if (ra !== rb) return ra - rb;
  if (ra === ROS_ACTIVITY_RANK.length) {
    const c = String(a.activity).localeCompare(String(b.activity));
    if (c) return c;
  }
  const pc = String(a.project || '').localeCompare(String(b.project || ''));
  if (pc) return pc;
  if (a.source !== b.source) return a.source === 'eps' ? -1 : 1;
  if (a.source === 'manual' && (a.createdAt || '') !== (b.createdAt || '')) return (a.createdAt || '') < (b.createdAt || '') ? -1 : 1;
  return String(a.key).localeCompare(String(b.key));
}

/** The Backend's ros_sortEvents: by date and real time; a TBD row sits before the first later-stage timed row. */
export function sortRosEvents(events) {
  const byDate = {};
  (events || []).forEach((e) => { (byDate[e.dateISO] = byDate[e.dateISO] || []).push(e); });
  const out = [];
  Object.keys(byDate).sort().forEach((date) => {
    const day = byDate[date];
    const timed = day.filter(isTimed).sort(compareEvents);
    const untimed = day.filter((e) => !isTimed(e)).sort(compareEvents);
    const before = timed.map(() => []);
    const tail = [];
    untimed.forEach((u) => {
      const slot = timed.findIndex((t) => activityRank(t.activity) > activityRank(u.activity));
      (slot < 0 ? tail : before[slot]).push(u);
    });
    timed.forEach((t, i) => { before[i].forEach((u) => out.push(u)); out.push(t); });
    tail.forEach((u) => out.push(u));
  });
  return out;
}

/** "2:30 PM" → minutes after midnight; null for TBD or unreadable. */
export function clockMinutes(text) {
  const m = String(text || '').trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp])[Mm]$/);
  if (!m) return null;
  const h = parseInt(m[1], 10) % 12;
  return (m[3].toUpperCase() === 'P' ? h + 12 : h) * 60 + parseInt(m[2], 10);
}

/** Every search term must appear in the candidate's server-built search text. */
export function matchesSearch(candidate, query) {
  const terms = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = String(candidate.searchText || '').toLowerCase();
  return terms.every((t) => hay.indexOf(t) !== -1);
}

export function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `ev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
